import { EligibleCategory, EligibleFinanceProgram } from '@trnsact/trnsact-shared-types';
import { EligibleFinanceProgramTableRow } from '../types';
import { formatCurrency } from 'utils';

const formatTerms = (terms: string[] | number[] | null | undefined): string => {
  if (!terms?.length) return '-';
  return terms.join(', ');
};

const formatRate = (rate: number | null | undefined): string => {
  if (rate == null) return '-';
  return `${rate}%`;
};

const formatDownPayment = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return `${value}%`;
};

const getMaxTerm = (terms: Array<{ term: number }> | string[] | number[] | null | undefined): string => {
  if (!terms?.length) return '-';
  const numericTerms = terms.map(t => (typeof t === 'object' ? t.term : Number(t)));
  return Math.max(...numericTerms).toString();
};

const buildProgramRow = (program: EligibleFinanceProgram, selectable: boolean): EligibleFinanceProgramTableRow => {
  const config = program.paymentOptionsConfiguration as Record<string, any> | null;

  return {
    id: program.financeProgramId,
    name: program.nameInternal,
    isTier: false,
    selectable,
    isQualified: program.eligibleCategory === EligibleCategory.Criteria,
    isRecommended: false,
    financeProgramId: program.financeProgramId,
    tierName: null,
    maxAmount: formatCurrency(config?.maxApprovalAmount ?? config?.financeAmount) || '-',
    maxTerm: getMaxTerm(config?.terms),
    terms: formatTerms(config?.terms?.map((t: any) => (typeof t === 'object' ? t.term : t))),
    rate: formatRate(config?.terms?.[0]?.rate),
    downPayment: formatDownPayment(config?.minimumRequiredDownPayment),
  };
};

const buildTierRow = (
  program: EligibleFinanceProgram,
  tier: any,
  isRecommended: boolean
): EligibleFinanceProgramTableRow => ({
  id: `${program.financeProgramId}-${tier.name}`,
  name: tier.name,
  isTier: true,
  selectable: true,
  isQualified: true,
  isRecommended,
  financeProgramId: program.financeProgramId,
  tierName: tier.name,
  maxAmount: formatCurrency(tier.maxApprovalAmount) || '-',
  maxTerm: getMaxTerm(tier.terms),
  terms: formatTerms(tier.terms),
  rate: formatRate(tier.interestRate),
  downPayment: formatDownPayment(tier.suggestedDownPaymentPercentage),
});

export const buildScorecardRows = (programs: EligibleFinanceProgram[], vo: any): EligibleFinanceProgramTableRow[] => {
  const rows: EligibleFinanceProgramTableRow[] = [];

  programs.forEach(program => {
    const programRow = buildProgramRow(program, false);
    rows.push(programRow);

    const tiers = (program.scoreTiers?.tiers as any[]) ?? [];
    tiers.forEach(tier => {
      rows.push(buildTierRow(program, tier, vo?.scoreCardResult?.highestTierPassed === tier.name));
    });
  });

  return rows;
};

export const buildCriteriaRows = (programs: EligibleFinanceProgram[]): EligibleFinanceProgramTableRow[] =>
  programs.map(program => buildProgramRow(program, true));
